import {
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
  ChangeDetectionStrategy,
} from '@angular/core';
import { animate, style, transition, trigger } from '@angular/animations';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DataRepeaterComponent, FormSettings, Widget, WidgetComponentType } from 'app/campaign/widgets.model';
import WidgetComponentHelpers from 'app/campaign/widget-component-helpers';
import { SnackBarService } from 'app/core/snack-bar.service';
import { DataSourceModel, DataSourceType } from 'app/data-sources/data-sources.models';
import { DataSourcesService } from 'app/data-sources/data-sources.service';
import { WidgetService } from 'app/shared/services/widget.service';

@Component({
  selector: 'app-flyout-form-settings-leaderboard',
  templateUrl: './flyout-form-settings-leaderboard.component.html',
  styleUrl: './flyout-form-settings-leaderboard.component.scss',
  standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
  animations: [
    trigger('expandReveal', [
      transition(':enter', [
        style({ height: '0', opacity: 0, overflow: 'hidden' }),
        animate('200ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '*', opacity: 1 })),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate('150ms cubic-bezier(0.4, 0, 0.2, 1)', style({ height: '0', opacity: 0 })),
      ]),
    ]),
  ],
})
export class FlyoutFormSettingsLeaderboardComponent implements OnChanges {
  @Input() public widget: Widget | null = null;

  @Output() public readonly dataSourceChanged = new EventEmitter<void>();

  private readonly dataSourcesService = inject(DataSourcesService);
  private readonly widgetService = inject(WidgetService);
  private readonly snackBarService = inject(SnackBarService);
  private readonly destroyRef = inject(DestroyRef);

  public expanded = false;
  public saving = false;
  public hasScore = false;
  public supportedDataSources: DataSourceModel[] = [];
  public dataSourceId: string | null = null;
  /** The leaderboard the widget's own data-repeater renders, if any (drives the null-reconciliation). */
  private dataSourceIdOnComponent: string | null = null;
  private dataSourcesSubscribed = false;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['widget'] && this.widget) {
      this.initFromWidget();
      this.ensureDataSourcesLoaded();
    }
  }

  private initFromWidget(): void {
    if (!this.widget) {
      return;
    }
    this.dataSourceId = this.widget.formSettings?.dataSourceId ?? null;

    const repeater = WidgetComponentHelpers.getComponentByType(this.widget, WidgetComponentType.DataRepeater) as
      DataRepeaterComponent | undefined;
    if (repeater?.dataRepeaterSettings?.dataSourceType === DataSourceType.Leaderboard) {
      this.dataSourceId = this.widget.formSettings?.dataSourceId ?? repeater.dataRepeaterSettings.dataSourceId ?? null;
      this.dataSourceIdOnComponent = repeater.dataRepeaterSettings.dataSourceId ?? null;
    }

    this.hasScore = WidgetComponentHelpers.getAllComponentsOnWidget(this.widget).some((component) =>
      (component.listItems ?? []).some((item) => item.score != null),
    );
  }

  private ensureDataSourcesLoaded(): void {
    if (this.dataSourcesSubscribed) {
      return;
    }
    this.dataSourcesSubscribed = true;
    this.dataSourcesService.dataSources$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((dataSources) => {
      this.supportedDataSources = (dataSources ?? []).filter((source) => source.type === DataSourceType.Leaderboard);
    });
    if (!this.dataSourcesService.dataSources) {
      this.dataSourcesService.loadDataSources();
    }
  }

  public toggleExpansion(): void {
    this.expanded = !this.expanded;
  }

  public onDataSourceChange(): void {
    if (!this.widget) {
      return;
    }
    // If the chosen leaderboard is the one the widget's repeater already renders, the repeater owns it —
    // store null in form settings rather than duplicating the id.
    const dataSourceId = this.dataSourceIdOnComponent !== this.dataSourceId ? this.dataSourceId : null;
    const payload = { ...(this.widget.formSettings ?? {}), dataSourceId } as FormSettings;

    this.saving = true;
    this.widgetService
      .patchWidget(this.widget.id, '/formSettings', payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          if (this.widget) {
            this.widget.formSettings = payload;
          }
          this.widgetService.hasUnpublishedChangesBS.next(true);
          this.saving = false;
          this.dataSourceChanged.emit();
          this.snackBarService.success('generic.success');
        },
        error: () => {
          this.saving = false;
          this.snackBarService.error('generic.errorOccurred');
        },
      });
  }
}
